import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';
import { withRateLimitHandling } from '@/lib/rateLimitHandler';

export interface TimelineEvent {
  id: string;
  type: 'email' | 'meeting';
  date: string;
  title: string;
  description: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  metadata?: {
    direction?: string;
    isUrgent?: boolean;
    location?: string;
    attendees?: string[];
    hasAttachments?: boolean;
  };
}

export class ContactTimelineService {
  /**
   * Load all timeline events for a contact by matching their email address
   */
  async loadTimelineForContact(
    userId: string,
    contactEmail: string
  ): Promise<TimelineEvent[]> {
    try {
      const events: TimelineEvent[] = [];

      // Fetch emails from messages_enhanced table (newer schema) with rate limit handling
      const messagesEnhanced = await withRateLimitHandling(
        () => blink.db.messagesEnhanced.list({
          where: { userId: userId },
          orderBy: { sentDate: 'desc' },
          limit: 500,
        }),
        `timeline-messages-${userId}`,
        { maxRetries: 2, initialDelay: 2000, silent: true }
      );

      // Fetch emails from old emails table (legacy schema) with rate limit handling
      const legacyEmails = await withRateLimitHandling(
        () => blink.db.emails.list({
          where: { userId: userId, contactEmail: contactEmail },
          orderBy: { sentAt: 'desc' },
          limit: 100,
        }),
        `timeline-emails-${userId}-${contactEmail}`,
        { maxRetries: 2, initialDelay: 2000, silent: true }
      );

      // Fetch calendar events from calendar_events_enhanced with rate limit handling
      const calendarEventsEnhanced = await withRateLimitHandling(
        () => blink.db.calendarEventsEnhanced.list({
          where: { userId: userId },
          orderBy: { startTime: 'desc' },
          limit: 200,
        }),
        `timeline-cal-enhanced-${userId}`,
        { maxRetries: 2, initialDelay: 2000, silent: true }
      );

      // Fetch calendar events from old calendar_events table with rate limit handling
      const legacyCalendarEvents = await withRateLimitHandling(
        () => blink.db.calendarEvents.list({
          where: { userId: userId },
          orderBy: { startTime: 'desc' },
          limit: 100,
        }),
        `timeline-cal-legacy-${userId}`,
        { maxRetries: 2, initialDelay: 2000, silent: true }
      );

      // Process messages_enhanced - match sender or recipients
      messagesEnhanced.forEach((msg: any) => {
        const lowerContactEmail = contactEmail.toLowerCase();
        const senderEmail = msg.senderEmail?.toLowerCase() || '';
        
        // Parse recipients arrays
        const toRecipients = this.parseRecipients(msg.toRecipients);
        const ccRecipients = this.parseRecipients(msg.ccRecipients);
        const bccRecipients = this.parseRecipients(msg.bccRecipients);
        
        const allRecipients = [...toRecipients, ...ccRecipients, ...bccRecipients];
        
        // Check if contact is sender or recipient
        const isMatch = 
          senderEmail === lowerContactEmail ||
          allRecipients.some(r => r.toLowerCase() === lowerContactEmail);

        if (isMatch) {
          const date = msg.sentDate || msg.receivedDate || msg.createdAt;
          events.push({
            id: `msg-enhanced-${msg.id}`,
            type: 'email',
            date,
            title: msg.subject || 'No subject',
            description: msg.aiSummary || msg.snippet || msg.bodyText?.substring(0, 200) || 'No content',
            sentiment: this.determineSentiment(msg),
            metadata: {
              direction: senderEmail === lowerContactEmail ? 'received' : 'sent',
              isUrgent: Number(msg.isImportant) > 0,
              hasAttachments: Number(msg.hasAttachments) > 0,
            },
          });
        }
      });

      // Process legacy emails table
      legacyEmails.forEach((email: any) => {
        events.push({
          id: `email-legacy-${email.id}`,
          type: 'email',
          date: email.sentAt || email.createdAt,
          title: email.subject || 'No subject',
          description: email.aiSummary || email.body?.substring(0, 200) || 'No content',
          sentiment: Number(email.isUrgent) > 0 ? 'negative' : 'neutral',
          metadata: {
            direction: email.direction,
            isUrgent: Number(email.isUrgent) > 0,
          },
        });
      });

      // Process calendar_events_enhanced - check attendees
      calendarEventsEnhanced.forEach((event: any) => {
        if (this.isContactInEvent(contactEmail, event)) {
          events.push({
            id: `cal-enhanced-${event.id}`,
            type: 'meeting',
            date: event.startTime,
            title: event.title || 'Meeting',
            description: event.aiSummary || event.description || 'No description',
            sentiment: 'positive',
            metadata: {
              location: event.location || undefined,
              attendees: this.parseAttendees(event.attendees),
            },
          });
        }
      });

      // Process legacy calendar events
      legacyCalendarEvents.forEach((event: any) => {
        const attendees = event.attendees || '';
        if (attendees.toLowerCase().includes(contactEmail.toLowerCase())) {
          events.push({
            id: `cal-legacy-${event.id}`,
            type: 'meeting',
            date: event.startTime,
            title: event.title || 'Meeting',
            description: event.aiSummary || event.description || 'No description',
            sentiment: 'positive',
            metadata: {
              location: event.location || undefined,
              attendees: attendees.split(',').map((a: string) => a.trim()).filter(Boolean),
            },
          });
        }
      });

      // Sort all events by date (newest first)
      events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Remove duplicates based on similar content and date
      const uniqueEvents = this.deduplicateEvents(events);

      return uniqueEvents;
    } catch (error) {
      console.error('Failed to load timeline for contact:', error);
      return [];
    }
  }

  /**
   * Parse recipients from JSON string or array
   */
  private parseRecipients(recipientsData: any): string[] {
    if (!recipientsData) return [];
    
    try {
      // If it's already an array
      if (Array.isArray(recipientsData)) {
        return recipientsData.map(r => {
          if (typeof r === 'string') return r;
          if (typeof r === 'object' && r.email) return r.email;
          return '';
        }).filter(Boolean);
      }
      
      // If it's a JSON string
      if (typeof recipientsData === 'string') {
        const parsed = safeJsonParse(recipientsData, 'contactTimelineService.parseRecipients');
        if (Array.isArray(parsed)) {
          return parsed.map(r => {
            if (typeof r === 'string') return r;
            if (typeof r === 'object' && r.email) return r.email;
            return '';
          }).filter(Boolean);
        }
      }
      
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Parse attendees from event data
   */
  private parseAttendees(attendeesData: any): string[] {
    if (!attendeesData) return [];
    
    try {
      if (Array.isArray(attendeesData)) {
        return attendeesData.map(a => {
          if (typeof a === 'string') return a;
          if (typeof a === 'object' && a.email) return a.email;
          return '';
        }).filter(Boolean);
      }
      
      if (typeof attendeesData === 'string') {
        // Try JSON parse first
        try {
          const parsed = safeJsonParse(attendeesData, 'contactTimelineService.parseAttendees');
          if (Array.isArray(parsed)) {
            return parsed.map(a => {
              if (typeof a === 'string') return a;
              if (typeof a === 'object' && a.email) return a.email;
              return '';
            }).filter(Boolean);
          }
        } catch {
          // Fall back to comma-separated string
          return attendeesData.split(',').map(a => a.trim()).filter(Boolean);
        }
      }
      
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Check if contact email is in calendar event attendees
   */
  private isContactInEvent(contactEmail: string, event: any): boolean {
    const lowerContactEmail = contactEmail.toLowerCase();
    
    // Check organizer
    if (event.organizerEmail?.toLowerCase() === lowerContactEmail) {
      return true;
    }
    
    // Check attendees
    const attendees = this.parseAttendees(event.attendees);
    return attendees.some(email => email.toLowerCase() === lowerContactEmail);
  }

  /**
   * Determine email sentiment based on flags
   */
  private determineSentiment(msg: any): 'positive' | 'neutral' | 'negative' {
    if (msg.aiSentiment) {
      return msg.aiSentiment as 'positive' | 'neutral' | 'negative';
    }
    
    if (Number(msg.isImportant) > 0 || Number(msg.isStarred) > 0) {
      return 'positive';
    }
    
    if (Number(msg.isSpam) > 0 || Number(msg.isTrash) > 0) {
      return 'negative';
    }
    
    return 'neutral';
  }

  /**
   * Remove duplicate events based on title, date proximity, and type
   */
  private deduplicateEvents(events: TimelineEvent[]): TimelineEvent[] {
    const seen = new Map<string, TimelineEvent>();
    
    events.forEach(event => {
      // Create a fuzzy key based on type, title, and date (within 1 minute)
      const date = new Date(event.date);
      const dateKey = Math.floor(date.getTime() / 60000); // Round to minute
      const titleKey = event.title.toLowerCase().trim().substring(0, 50);
      const fuzzyKey = `${event.type}-${titleKey}-${dateKey}`;
      
      // Keep the first occurrence (newest due to pre-sorting)
      if (!seen.has(fuzzyKey)) {
        seen.set(fuzzyKey, event);
      }
    });
    
    return Array.from(seen.values());
  }

  /**
   * Get timeline statistics for a contact
   */
  async getTimelineStats(
    userId: string,
    contactEmail: string
  ): Promise<{
    totalEmails: number;
    totalMeetings: number;
    firstInteraction?: string;
    lastInteraction?: string;
    avgResponseTime?: number;
  }> {
    const events = await this.loadTimelineForContact(userId, contactEmail);
    
    const emails = events.filter(e => e.type === 'email');
    const meetings = events.filter(e => e.type === 'meeting');
    
    const dates = events.map(e => new Date(e.date).getTime()).sort();
    
    return {
      totalEmails: emails.length,
      totalMeetings: meetings.length,
      firstInteraction: dates.length > 0 ? new Date(dates[0]).toISOString() : undefined,
      lastInteraction: dates.length > 0 ? new Date(dates[dates.length - 1]).toISOString() : undefined,
    };
  }
}

export const contactTimelineService = new ContactTimelineService();
